import axios from 'axios';
import { API_BASE_URL, getAuthHeader } from '../config/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const authHeaders = getAuthHeader();
    if (authHeaders.Authorization) {
      config.headers.Authorization = authHeaders.Authorization;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor to handle common errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If error is 401 and we haven't tried to refresh the token yet
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Try to refresh the token
        const refreshToken = localStorage.getItem('adminRefreshToken');

        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}/users/token/refresh/`, {
            refresh: refreshToken
          });

          if (response.data.access) {
            // Update the token in localStorage
            localStorage.setItem('adminToken', response.data.access);

            // Update the Authorization header
            originalRequest.headers.Authorization = `Bearer ${response.data.access}`;

            // Retry the original request
            return axios(originalRequest);
          }
        }

        // If refresh token is not available or refresh failed, logout
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminRefreshToken');
        localStorage.removeItem('adminUser');
        window.location.href = '/login';
      } catch (refreshError) {
        // If refresh token is expired or invalid, logout
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminRefreshToken');
        localStorage.removeItem('adminUser');
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

// API utility functions
export const apiGet = async (url, params = {}) => {
  try {
    console.log(`Making GET request to: ${API_BASE_URL}${url}`, { params });
    const response = await api.get(url, { params });
    console.log(`GET response from ${url}:`, response.data);
    return response.data;
  } catch (error) {
    console.error(`Error in GET request to ${url}:`, error.response || error);
    throw handleApiError(error);
  }
};

export const apiPost = async (url, data = {}) => {
  try {
    const response = await api.post(url, data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export const apiPut = async (url, data = {}) => {
  try {
    const response = await api.put(url, data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export const apiPatch = async (url, data = {}) => {
  try {
    const response = await api.patch(url, data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export const apiDelete = async (url) => {
  try {
    const response = await api.delete(url);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Upload file with form data (POST)
export const apiUpload = async (url, formData) => {
  try {
    const token = localStorage.getItem('adminToken');
    const headers = {};

    // Add authorization header if token exists
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Important: Do NOT set Content-Type header manually
    // The browser will set it automatically with the correct boundary

    console.log('Uploading (POST) to:', url);
    console.log('Headers:', headers);
    console.log('FormData entries:');
    for (let pair of formData.entries()) {
      console.log(pair[0] + ': ' + (pair[1] instanceof File ? pair[1].name : pair[1]));
    }

    // Use axios directly to have more control
    const response = await api({
      method: 'post',
      url: url,
      data: formData,
      headers: headers,
      // Important: Set these options for proper file upload
      transformRequest: (data, headers) => {
        // Do not transform the data
        return data;
      }
    });

    return response.data;
  } catch (error) {
    console.error('Upload error:', error.response || error);
    throw handleApiError(error);
  }
};

// Upload file with form data (PUT)
export const apiUploadPut = async (url, formData) => {
  try {
    const token = localStorage.getItem('adminToken');
    const headers = {};

    // Add authorization header if token exists
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Important: Do NOT set Content-Type header manually
    // The browser will set it automatically with the correct boundary

    console.log('Uploading (PUT) to:', url);
    console.log('Headers:', headers);
    console.log('FormData entries:');
    for (let pair of formData.entries()) {
      console.log(pair[0] + ': ' + (pair[1] instanceof File ? pair[1].name : pair[1]));
    }

    // Use axios directly to have more control
    const response = await api({
      method: 'put',
      url: url,
      data: formData,
      headers: headers,
      // Important: Set these options for proper file upload
      transformRequest: (data, headers) => {
        // Do not transform the data
        return data;
      }
    });

    return response.data;
  } catch (error) {
    console.error('Upload error:', error.response || error);
    throw handleApiError(error);
  }
};

// Error handling helper
const handleApiError = (error) => {
  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    const { data, status } = error.response;

    console.log('API Error Response:', data);

    // Handle different error formats
    let errorMessage = 'Une erreur est survenue';
    let errorDetails = {};

    if (typeof data === 'string') {
      errorMessage = data;
    } else if (data.detail) {
      if (typeof data.detail === 'string') {
        errorMessage = data.detail;
      } else if (typeof data.detail === 'object') {
        // Handle Django REST Framework validation errors
        errorMessage = 'Erreur de validation';
        errorDetails = data.detail;
      }
    } else if (data.message) {
      errorMessage = data.message;
    } else if (data.error) {
      errorMessage = data.error;
    }

    return {
      status,
      message: errorMessage,
      errors: data.errors || errorDetails || {},
    };
  } else if (error.request) {
    // The request was made but no response was received
    console.log('No response received:', error.request);
    return {
      status: 0,
      message: 'Aucune réponse du serveur. Veuillez vérifier votre connexion internet.',
      errors: {},
    };
  } else {
    // Something happened in setting up the request that triggered an Error
    console.log('Request setup error:', error.message);
    return {
      status: 0,
      message: error.message || 'Une erreur est survenue',
      errors: {},
    };
  }
};

export default api;
